import type { AboutOrderSopSection } from "../types";
import {
  CREATE_ORDER_SOP_SECTIONS,
  ISSUE_INVOICE_SOP_SECTIONS,
  MANAGE_ORDER_SOP_SECTIONS,
  ORDER_RUN_BOOK_BEFORE_YOU_START_SECTION,
  ORDER_RUN_BOOK_ORDER_STATUSES_SECTION,
  RECORD_PAYMENT_SOP_SECTIONS
} from "./aboutOrderSopContent";
import {
  PAGE_ROUTE_CREATE_ORDER_SOP,
  PAGE_ROUTE_ISSUE_INVOICE_SOP,
  PAGE_ROUTE_MANAGE_ORDER_SOP,
  PAGE_ROUTE_RECORD_PAYMENT_SOP
} from "@kivunova/kivufrontendcommon";

export type OrderRunBookGuide = {
  id: string;
  route: string;
  pageTestId: string;
  titleKey: string;
  titleFallback: string;
  introKey: string;
  introFallback: string;
  noteKey: string;
  noteFallback: string;
  sections: AboutOrderSopSection[];
};

const withBeforeYouStart = (
  sections: AboutOrderSopSection[]
): AboutOrderSopSection[] => [
  ORDER_RUN_BOOK_BEFORE_YOU_START_SECTION,
  ...sections
];

const withManageOrderIntro = (
  sections: AboutOrderSopSection[]
): AboutOrderSopSection[] => [
  ORDER_RUN_BOOK_BEFORE_YOU_START_SECTION,
  ORDER_RUN_BOOK_ORDER_STATUSES_SECTION,
  ...sections
];

export const ORDER_RUN_BOOK_GUIDES: OrderRunBookGuide[] = [
  {
    id: "create-order",
    route: PAGE_ROUTE_CREATE_ORDER_SOP,
    pageTestId: "create-order-sop-page",
    titleKey: "create-order-sop-page-title",
    titleFallback: "How to create an order",
    introKey: "create-order-sop-page-intro",
    introFallback:
      "Open Sales Orders, start a new sale, and pick the store and customer.",
    noteKey: "create-order-sop-page-note",
    noteFallback:
      "The new order starts as Draft. Next, add products and confirm the sale — see Manage order.",
    sections: withBeforeYouStart(CREATE_ORDER_SOP_SECTIONS)
  },
  {
    id: "manage-order",
    route: PAGE_ROUTE_MANAGE_ORDER_SOP,
    pageTestId: "manage-order-sop-page",
    titleKey: "manage-order-sop-page-title",
    titleFallback: "How to manage an order",
    introKey: "manage-order-sop-page-intro",
    introFallback:
      "Add or scan products, edit the draft, review totals, and confirm with the customer.",
    noteKey: "manage-order-sop-page-note",
    noteFallback:
      "After you confirm, the order moves to Offer accepted. You can then issue an invoice.",
    sections: withManageOrderIntro(MANAGE_ORDER_SOP_SECTIONS)
  },
  {
    id: "issue-invoice",
    route: PAGE_ROUTE_ISSUE_INVOICE_SOP,
    pageTestId: "issue-invoice-sop-page",
    titleKey: "issue-invoice-sop-page-title",
    titleFallback: "How to issue an invoice",
    introKey: "issue-invoice-sop-page-intro",
    introFallback: "Create a bill for the customer once they agree to buy.",
    noteKey: "issue-invoice-sop-page-note",
    noteFallback:
      "The order shows Pending payment until the customer pays. Record each payment on the next guide.",
    sections: withBeforeYouStart(ISSUE_INVOICE_SOP_SECTIONS)
  },
  {
    id: "record-payment",
    route: PAGE_ROUTE_RECORD_PAYMENT_SOP,
    pageTestId: "record-payment-sop-page",
    titleKey: "record-payment-sop-page-title",
    titleFallback: "How to record payment",
    introKey: "record-payment-sop-page-intro",
    introFallback:
      "Log full or partial payments, then close the sale when everything is settled.",
    noteKey: "record-payment-sop-page-note",
    noteFallback:
      "When the balance reaches zero the order shows Paid. Use Complete Order to finish and update stock.",
    sections: withBeforeYouStart(RECORD_PAYMENT_SOP_SECTIONS)
  }
];

export const ORDER_RUN_BOOK_GUIDE_BY_ID = Object.fromEntries(
  ORDER_RUN_BOOK_GUIDES.map((guide) => [guide.id, guide])
) as Record<string, OrderRunBookGuide>;
